/**
 * Тема интерфейса.
 *
 * Тёмная остаётся основной — проект про старые снимки, и они смотрятся на
 * тёмном лучше. Светлая нужна тем, кому тёмный интерфейс неудобен днём или
 * просто непривычен.
 *
 * Выбор запоминается в браузере, а не в профиле: тема — свойство устройства
 * и освещения вокруг, а не человека. С телефона на солнце хочется светлую,
 * с того же аккаунта вечером за компьютером — тёмную.
 */
import { create } from "zustand";

export type Theme = "dark" | "light";

const STORAGE_KEY = "chronoguessr.theme";

/**
 * Тема, выбранная раньше. По умолчанию тёмная.
 *
 * Системная настройка сознательно не учитывается: у большинства она светлая,
 * и проект встречал бы почти всех не тем лицом, которое ему задумано. Тёмная
 * тема здесь — часть замысла, а не предпочтение по умолчанию, поэтому светлую
 * человек выбирает сам, и выбор запоминается.
 */
export function initialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    // Хранилище недоступно — не повод падать: тема проживёт до конца вкладки.
  }
  return "dark";
}

/** Проставляет тему на документе: по этому атрибуту переключается палитра. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

interface ThemeState {
  theme: Theme;
  /** Выбрать тему явно: в меню видны оба значения, а не одно переключение. */
  set: (theme: Theme) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: initialTheme(),
  set: (theme) => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Выбор проживёт до конца вкладки.
    }
    set({ theme });
  },
  toggle: () => get().set(get().theme === "dark" ? "light" : "dark"),
}));
